<template>
  <v-container
    v-if="company"
    class="layout-container"
  >
    <BaseHeader
      :title="$t('legal-consents.title')"
      :style-variant="10"
    >
      <template
        v-if="!isPlatformBottimmo"
        v-slot:design
      >
        <HeaderDesign />
      </template>
    </BaseHeader>

    <v-card
      v-if="isContractOwner"
      border
      style="overflow: hidden"
    >
      <v-row>
        <v-col
          cols="12"
          class="pa-6"
        >
          <v-alert
            v-if="showUpdateWarning"
            type="warning"
            class="my-4 mr-4"
          >
            {{ $t('alerts.legal-consents.warning') }}
          </v-alert>

          <div class="pt-2">
            <v-checkbox
              v-for="item in preOnboardingItems"
              :key="item.identifier"
              v-model="acceptedIdentifiers"
              :value="item.identifier"
              :disabled="!canAccept"
              hide-details
            >
              <template v-slot:label>
                {{ itemDisplayText(item) }}
                <span v-if="item.required === false"> ({{ $t('labels.optional') }})</span>
                <div
                  v-if="item.url"
                  class="d-flex align-center pl-4"
                >
                  <v-icon class="mr-2"> mdi-file </v-icon>
                  <a
                    :href="item.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-decoration-none"
                    @click.stop
                  >
                    <h4 class="pb-0 mb-0">
                      {{ item.name }}
                      <v-icon
                        color="primary"
                        size="14"
                      >
                        mdi-open-in-new
                      </v-icon>
                    </h4>
                  </a>
                </div>
              </template>
            </v-checkbox>
          </div>

          <div
            v-if="canAccept"
            class="justify-end d-flex"
          >
            <v-btn
              class="primary"
              :disabled="!allRequiredAccepted"
              @click="submit"
            >
              {{ $t('buttons.avv.cta') }}
            </v-btn>
          </div>
          <v-btn
            v-else
            :href="postAcceptPath"
          >
            {{ $t('legal-consents.start-onboarding') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card>
    <v-card
      v-else
      border
      class="pa-8"
    >
      <p v-html="$t('legal-consents.not-accepted-text', { readablePlatformName })" />
    </v-card>
  </v-container>
</template>

<script>
import BaseHeader from '@/components/BaseHeader.vue'
import bus, { eventNames } from '@/lib/eventBus'
import brandingMixin from '@/mixins/branding'
import featureMixin from '@/mixins/feature'
import { submitFieldConsents } from '@/modules/iubenda/submitFieldConsents'
import {
  buildConsentSubmissionFields,
  buildLegalConsentMutationInput,
} from '@/modules/legalConsents/buildConsentFields'
import { needsReacceptance } from '@/modules/legalConsents/needsReacceptance'
import { resolveConsentItemText } from '@/modules/legalConsents/resolveConsentItemText'
import HeaderDesign from '@/modules/licenseAgreement/HeaderDesign.vue'
import ACCEPT_LEGAL_CONSENTS from '@/queries/AcceptLegalConsents.gql'
import LEGAL_CONSENTS from '@/queries/LegalConsents.gql'

export default {
  components: { BaseHeader, HeaderDesign },
  mixins: [featureMixin, brandingMixin],
  data() {
    return {
      acceptedIdentifiers: [],
      canAccept: true,
    }
  },
  computed: {
    isContractOwner() {
      return this.$auth.user.isContractOwner
    },
    items() {
      return this.getFeature(this.featureNames.LEGAL_CONSENTS).config.items || []
    },
    preOnboardingItems() {
      return this.items.filter((item) => item.context.includes('pre-onboarding'))
    },
    requiredItems() {
      return this.preOnboardingItems.filter((item) => item.required !== false)
    },
    legalConsentRecords() {
      return this.company?.legalConsents || []
    },
    updatedLegalConsentsExist() {
      return needsReacceptance(this.legalConsentRecords, this.preOnboardingItems)
    },
    showUpdateWarning() {
      return this.legalConsentRecords.length > 0 && this.updatedLegalConsentsExist
    },
    allRequiredAccepted() {
      return this.requiredItems.every((item) => this.acceptedIdentifiers.includes(item.identifier))
    },
    isOnboardingCompany() {
      return !!this.company.state?.stringValues?.includes('ONBOARDING')
    },
    postAcceptPath() {
      return this.isOnboardingCompany ? '/onboarding' : '/dashboard'
    },
  },
  methods: {
    itemDisplayText(item) {
      return resolveConsentItemText(item, this.$t, this.$te)
    },

    updateAcceptUI() {
      const isAccepted = this.legalConsentRecords.length > 0 && !this.updatedLegalConsentsExist
      this.canAccept = !isAccepted
      this.acceptedIdentifiers = isAccepted ? this.requiredItems.map((item) => item.identifier) : []
    },

    async submit() {
      try {
        const acceptedItems = this.preOnboardingItems.filter((item) =>
          this.acceptedIdentifiers.includes(item.identifier)
        )

        await this.$apollo.mutate({
          mutation: ACCEPT_LEGAL_CONSENTS,
          variables: {
            companyId: this.$auth.user.companyId,
            items: buildLegalConsentMutationInput(acceptedItems, this.$t, this.$te),
          },
          refetchQueries: [{ query: LEGAL_CONSENTS, variables: { id: this.$auth.user.companyId } }],
        })

        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'success',
          text: this.$t('alerts.legal-consents.success'),
        })

        this.updateAcceptUI()
        submitFieldConsents(buildConsentSubmissionFields(acceptedItems, this.$te), this.$t)
        this.$router.push(this.postAcceptPath)
      } catch (err) {
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: this.$t('alerts.legal-consents.error'),
        })
      }
    },
  },
  apollo: {
    company: {
      query: LEGAL_CONSENTS,
      variables() {
        return {
          id: this.$auth.user.companyId,
        }
      },
      result({ data }) {
        if (!data?.company) return
        this.updateAcceptUI()
      },
      skip() {
        return this.$auth.loading || !this.$auth.isAuthenticated
      },
    },
  },
}
</script>
